import React, { useCallback, useMemo } from "react";
import { useSubscribeToPagesExtended, useSubToProject } from "../../api/api";
import { usePageNavigation } from "./NavigationProvider";
import { SubToPages_pages } from "../../api/SubToPages";
import { SubToProject_project } from "../../api/__generated__/SubToProject";

/** Passive selection tracking never requests a scroll. */
export function useUpdateDisplayedPageNumber() {
  const { recordCurrentPage } = usePageNavigation();
  return useCallback((number: string) => recordCurrentPage(Number(number)), [recordCurrentPage]);
}

/**
 * Misc.
 */

export function usePages(projectId: string, skip = false) {
  const { loading, data, error } = useSubscribeToPagesExtended({ variables: { projectId }, skip });

  const pages = useMemo(() => data?.pages || [], [data]);

  return { pages, loading, error };
}

type ProjectContextData = {
  pageHeaders?: ReturnType<typeof import("../../api/pagePresentation").indexPageHeaders>;
  project: SubToProject_project;
  pages: SubToPages_pages[];
};

const ProjectContext = React.createContext<ProjectContextData | null>(null);
export const ProjectProvider = ProjectContext.Provider;

export const useProjectContext = () => {
  const context = React.useContext(ProjectContext);
  if (!context) {
    throw new Error("useProjectContext must be used within a ProjectProvider");
  }
  return context;
};

export function useProject({
  projectId,
  withInfo = false,
  withPages = false,
  withAudioInfo = false,
}: {
  projectId: string;
  withInfo?: boolean;
  withPages?: boolean;
  withAudioInfo?: boolean;
}) {
  const { data: book, loading: bookLoading } = useSubToProject({
    id: projectId,
    withInfo,
    withAudioInfo,
  });

  const { pages, loading: pagesLoading } = usePages(projectId, !withPages);

  return {
    project: book?.project[0],
    pages,
    bookLoading,
    pagesLoading,
  };
}
